import {
  AdvancesSidebarIcon,
  AlertsSidebarIcon,
  ApprovalsSidebarIcon,
  MessagesSidebarIcon,
  PaperworkSidebarIcon,
  PricingToolSidebarIcon,
  SidebarLTLIcon,
} from '@assets';
import { Box, Skeleton } from '@atgfr8/atg-ui-tailwind';
import { ToastType } from '@components/Toast/Toast';
import { domainUrl } from '@constants/urls';
import { useNavigate } from '@solidjs/router';
import {
  featureFlagStore,
  isFeatureFlagEnabled,
} from '@store/featureFlags/store';
import {
  getAllAlertsData,
  getAllAnnouncementData,
  getAllApprovalsData,
  getAllNeedPaperworkData,
  getAllPendingChecksData,
  getAllPendingCustomers,
  getCreditIncreaseRequestCustomers,
  getDoNotSolicitRequests,
  getEscalations,
  getMaxRateRequestsData,
} from '@store/Global/actions';
import { setGlobalState } from '@store/Global/store';
import { sidebarDataStore } from '@store/index';
import { setCurrentLTLQuoteView } from '@store/ltl/actions';
import {
  canViewMaxRateApprovals,
  getPendingMaxRateRequestCount,
} from '@store/orders/actions/rateRequestActions';
import { requestQuoteFormReset } from '@store/ltl/quoteFormService';
import { resetLtlReferenceNumberStore } from '@store/ltl/store';
import { userStore } from '@store/user';
import { AccountIcon, AnnouncementsIcon } from '@atgfr8/icons';
import { ConfigManager } from '@utils/ConfigManager';
import {
  handleToast,
  hasPermission,
  isAdmin,
  isSuperAdmin,
} from '@utils/utils';
import { getCarrierApprovalsTitle } from '@views/collapsableSidebar/carrierApprovals/CarrierApprovals';
import { userHasPermission } from '@views/order/components/carrier/stops/stopsEditor/utilFunctions';
import { filter } from 'lodash';
import { For, Setter, Show, createSignal, onMount } from 'solid-js';
import { FeatureFlags } from '@constants/featureFlags';

import cls from './classes';
import {
  flyoutsMuted,
  mutePreferenceReady,
  writeSidebarFlyoutMutePreference,
} from './helpers';

export type SidebarMenuLabelTypes =
  | 'Announcements'
  | 'Approvals'
  | 'Resolution Queue'
  | 'Advances'
  | 'Customers'
  | 'New Carriers'
  | 'Alerts'
  | 'Paperwork'
  | 'LTL'
  | 'Pricing Tool'
  | 'Pending Checks'
  | '';

type Props = {
  setChecked: Setter<SidebarMenuLabelTypes>;
  checked: () => SidebarMenuLabelTypes;
  isUserLoading: boolean;
};

export default function Sidebar(props: Props) {
  const [isLoading, setIsLoading] = createSignal(false);

  const navigate = useNavigate();

  const showMuteToggle = () =>
    isFeatureFlagEnabled(FeatureFlags.SidePanelMuteNotifications)();

  const menuItems = [
    {
      label: 'Announcements',
      icon: <AnnouncementsIcon class="text-[#468DB5]! size-7!" />,
      hide: () => false,
    },
    {
      label: 'Customers',
      icon: <AccountIcon class="text-[#468DB5]! size-7!" />,
      hide: () =>
        !isAdmin() ||
        !isFeatureFlagEnabled(FeatureFlags.SideBarCustomerQueue)(),
    },
    {
      label: 'Pending Checks',
      title: getCarrierApprovalsTitle,
      icon: AdvancesSidebarIcon,
      hide: () =>
        !Boolean(hasPermission('Approve Checks')) && !canViewMaxRateApprovals(),
    },
    {
      label: 'Alerts',
      icon: AlertsSidebarIcon,
      hide: () => false,
    },
    {
      label: 'Approvals',
      icon: ApprovalsSidebarIcon,
      hide: () =>
        isAdmin() || isFeatureFlagEnabled(FeatureFlags.PendingApprovalsV2)(),
    },
    {
      label: 'Resolution Queue',
      icon: ApprovalsSidebarIcon,
      hide: () =>
        isAdmin() || !isFeatureFlagEnabled(FeatureFlags.PendingApprovalsV2)(),
    },
    {
      label: 'Posting',
      icon: MessagesSidebarIcon,
      hide: () => !Boolean((userStore.user.postingGroupId ?? 0) > 0),
      link: '/Application/Posting',
    },
    {
      label: 'Paperwork',
      icon: PaperworkSidebarIcon,
      hide: () => isAdmin(),
    },
    {
      label: 'Pricing',
      icon: PricingToolSidebarIcon,
      hide: () => false,
      subMenu: [
        {
          label: 'Pricing Platform',
        },
        {
          label: 'Pricing Tool',
        },
      ],
    },
    {
      label: 'LTL',
      icon: SidebarLTLIcon,
      hide: () => !Boolean(userHasPermission('Rate LTL')),
      subMenu: [
        {
          label: 'LTL Quoting',
          link: '/ltl',
        },
        {
          label: 'Saved Quotes',
          link: '/savedquotes',
        },
      ],
    },
  ];

  const SidebarMenuSkeleton = () => (
    <div class="px-3 pt-2 flex flex-col gap-5">
      <For each={[1, 2, 3, 4, 5, 6]}>
        {() => (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={40}
            class="!bg-[#045E86]"
          />
        )}
      </For>
    </div>
  );

  onMount(async () => {
    try {
      if (featureFlagStore.loaded) {
        setIsLoading(true);
        await Promise.all([
          getAllAnnouncementData(),
          getAllPendingChecksData(),
          getAllAlertsData(),
          canViewMaxRateApprovals() ? getMaxRateRequestsData() : undefined,
        ]);
        if (!isAdmin()) {
          await getAllNeedPaperworkData();
          await getAllApprovalsData();
        }
        if (isAdmin() || isSuperAdmin()) {
          await getDoNotSolicitRequests();
          if (isFeatureFlagEnabled(FeatureFlags.SideBarCustomerQueue)()) {
            await getAllPendingCustomers();
            await getCreditIncreaseRequestCustomers();
          }
        }
        if (isFeatureFlagEnabled(FeatureFlags.CreditAppApi)()) {
          await getEscalations();
        }
      }
    } finally {
      setIsLoading(false);
    }
  });

  const Count = (props: { label: string }) => {
    const labels = {
      Announcements: 'annoucementsData',
      'Pending Checks': 'pendingChecksData',
      Customers: 'customersData',
      Paperwork: 'needPaperworkData',
      Alerts: 'alertsData',
      Approvals: 'approvalsData',
      'Resolution Queue': 'approvalsData',
    };

    const getCounts = () => {
      const countStrategies: Record<string, () => number | undefined> = {
        annoucementsData: () =>
          filter(sidebarDataStore.annoucementsData.data, { read: false })
            .length,
        alertsData: () =>
          filter(sidebarDataStore.alertsData.data, { read: false }).length,
        pendingChecksData: () =>
          (hasPermission('Approve Checks')
            ? sidebarDataStore.pendingChecksData.data.length
            : 0) +
          (canViewMaxRateApprovals()
            ? getPendingMaxRateRequestCount(
                sidebarDataStore.maxRateRequestsData.data,
              )
            : 0),
        customersData: () => {
          return (
            sidebarDataStore.pendingCustomers.data.length +
            sidebarDataStore.creditIncreaseRequestCustomers.data.length
          );
        },
        needPaperworkData: () => sidebarDataStore.needPaperworkData.data.length,
        approvalsData: () => {
          const { allApprovals, disputedLoads } =
            sidebarDataStore.approvalsData;
          if (allApprovals.length > 0 || disputedLoads.length > 0) {
            return allApprovals.filter((i) => i.needsAttention === true).length;
          }
        },
      };
      const labelKey = labels[props.label as keyof typeof labels];
      return labelKey ? countStrategies[labelKey]() : undefined;
    };

    return (
      <span
        class={`absolute -top-2 -right-2
          transition-all ease-linear duration-300
          rounded-full bg-[#D06679] border-[#B5112F] border text-white text-[10px] font-medium px-2 py-1 z-50 
          ${Boolean(getCounts()) ? 'visible' : 'invisible'}
          `}
      >
        {getCounts()}
      </span>
    );
  };

  const drawerList = () => (
    <Box class="mt-4">
      <Show
        when={!props.isUserLoading && !isLoading()}
        fallback={<SidebarMenuSkeleton />}
      >
        <For each={menuItems}>
          {(item) => {
            const [showSubMenu, setShowSubMenu] = createSignal(false);
            const [submenuTop, setSubmenuTop] = createSignal(0);
            let rowRef: HTMLDivElement | undefined;
            return (
              !Boolean(item.hide()) && (
                <div
                  ref={rowRef}
                  class={`border-l-4 h-12 w-full border-transparent cursor-pointer flex items-center  mb-2! hover:border-white hover:bg-[#01618D] ${
                    props.checked() === item.label
                      ? 'border-white bg-[#01618D]'
                      : ''
                  }`}
                  onMouseEnter={() => {
                    if (item.subMenu && rowRef) {
                      const rect = rowRef.getBoundingClientRect();
                      setSubmenuTop(rect.top + rect.height / 2);
                      setShowSubMenu(true);
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.stopImmediatePropagation();
                    e.stopPropagation();
                    item.subMenu && setShowSubMenu(false);
                  }}
                  onClick={() => {
                    if (item.label === 'LTL') {
                      setCurrentLTLQuoteView('quote-form');
                      resetLtlReferenceNumberStore();
                      requestQuoteFormReset();
                      return;
                    }
                    if (item.label === 'Pricing') return;
                    if (item.label === 'Posting') {
                      props.setChecked('');
                      window.open(`${item.link}`, '_blank');
                      return;
                    }
                    if (
                      item.label === 'Resolution Queue' &&
                      isFeatureFlagEnabled(FeatureFlags.PendingApprovalsV2)()
                    ) {
                      navigate('/PendingApprovalsTab');
                      return;
                    }
                    if (Boolean(item.link)) {
                      window.open(`${domainUrl}${item.link}`, '_blank');
                    } else {
                      props.setChecked(item.label as SidebarMenuLabelTypes);
                    }
                  }}
                >
                  <div
                    class={`flex h-full w-full items-center gap-1 px-0 ${
                      sidebarDataStore.isCollapsed
                        ? 'justify-center'
                        : 'justify-start'
                    }`}
                  >
                    <div
                      class="text-[#A5C6DA]! flex min-w-[36px] justify-center items-center"
                      title={item.title?.() ?? item.label}
                    >
                      <div class="relative inline-flex h-7 w-7 items-center justify-center">
                        {typeof item.icon === 'string' ? (
                          <img
                            src={item.icon}
                            alt={item.label}
                            class="h-7 w-7 object-contain block"
                          />
                        ) : (
                          item.icon
                        )}
                        <Count label={item.label} />
                      </div>
                    </div>
                    {!sidebarDataStore.isCollapsed && (
                      <span
                        class="text-[#ffffff]! flex-1"
                        title={item.title?.() ?? item.label}
                      >
                        {item.title?.() ?? item.label}
                      </span>
                    )}
                    {showSubMenu() && (
                      <Box
                        onMouseLeave={() => {
                          setShowSubMenu(false);
                        }}
                        style={{
                          position: 'fixed',
                          top: `${submenuTop()}px`,
                          transform: 'translateY(-50%)',
                          transition: 'all 0.3s ease-in-out',
                          left: sidebarDataStore.isCollapsed ? '78px' : '250px',
                          background: '#006C9D',
                          'z-index': 51,
                          width: '200px',
                        }}
                      >
                        <For each={item.subMenu}>
                          {(subItem) => (
                            <div
                              class="w-full p-0"
                              onClick={() => {
                                if (subItem.label === 'Pricing Tool') {
                                  window.open(
                                    '/Application/Pricing',
                                    'pricingToolWindow',
                                    'width=1200,height=900',
                                  );
                                  return;
                                }
                                if (subItem.label === 'Pricing Platform') {
                                  window.open(
                                    `https://pricing-platform${
                                      !!ConfigManager.env &&
                                      ConfigManager.env !== 'production' &&
                                      ConfigManager.env !== 'prod'
                                        ? '-dev'
                                        : ''
                                    }.atgfr8.com/`,
                                    'pricingPlatformWindow',
                                  );
                                  return;
                                }
                                if (
                                  'link' in subItem &&
                                  typeof subItem.link === 'string'
                                ) {
                                  navigate(subItem.link);
                                }
                              }}
                            >
                              <span class="text-[#ffffff]! hover:bg-[#01618D] block px-4 py-4 w-full">
                                {subItem.label}
                              </span>
                            </div>
                          )}
                        </For>
                      </Box>
                    )}
                  </div>
                </div>
              )
            );
          }}
        </For>
      </Show>
    </Box>
  );

  return (
    <>
      <div
        class={cls.container}
        style={{
          width: '78px',
        }}
      >
        <div class={`${cls.content} flex-1 min-h-0`}>{drawerList()}</div>
        <Show when={showMuteToggle() && mutePreferenceReady()}>
          <div class="border-t border-[#0F4561] px-2 py-3 flex justify-center">
            <label
              class="flex flex-col items-center gap-1 text-[10px] text-white/80 cursor-pointer"
              title="Mute sidebar flyouts"
            >
              <input
                type="checkbox"
                checked={flyoutsMuted()}
                aria-label="Mute sidebar flyouts"
                data-testid="mute-sidebar-flyouts-checkbox"
                class="h-4 w-4 cursor-pointer accent-[#A5C6DA]"
                onChange={(event) => {
                  const nextValue = event.currentTarget.checked;
                  if (nextValue) {
                    setGlobalState('alertBox', []);
                  }
                  void writeSidebarFlyoutMutePreference(nextValue).catch(
                    (error: unknown) => {
                      handleToast(ToastType.Error, (error as Error).message);
                    },
                  );
                }}
              />
              <span>Mute</span>
            </label>
          </div>
        </Show>
      </div>
    </>
  );
}
